import { t, type TranslationKey } from '../i18n';

type Child = Node | string | number | null | undefined | false;
type Props = {
  class?: string | undefined;
  text?: string | undefined;
  attrs?: Record<string, string | number | boolean | null | undefined> | undefined;
  on?: { [K in keyof HTMLElementEventMap]?: (e: HTMLElementEventMap[K]) => void } | undefined;
};

/** Small createElement helper. Strings are always inserted as text nodes. */
export function h<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  props?: Props | string | null,
  ...children: Child[]
): HTMLElementTagNameMap[K] {
  const el = document.createElement(tag);
  const p: Props = typeof props === 'string' ? { class: props } : (props ?? {});
  if (p.class) el.className = p.class;
  if (p.text !== undefined) el.textContent = p.text;
  for (const [name, value] of Object.entries(p.attrs ?? {})) {
    if (value === null || value === undefined || value === false) continue;
    el.setAttribute(name, value === true ? '' : String(value));
  }
  for (const [name, handler] of Object.entries(p.on ?? {})) {
    if (handler) el.addEventListener(name, handler as EventListener);
  }
  for (const child of children) {
    if (child === null || child === undefined || child === false) continue;
    el.append(child instanceof Node ? child : String(child));
  }
  return el;
}

/** Trusted static SVG markup (icons) → element. Never pass user content here. */
export function svg(markup: string, cls = 'icon'): HTMLElement {
  const span = document.createElement('span');
  span.className = cls;
  span.setAttribute('aria-hidden', 'true');
  span.innerHTML = markup;
  return span;
}

export function clear(el: Element | null): void {
  if (el) el.replaceChildren();
}

export function $(id: string): HTMLElement | null {
  return document.getElementById(id);
}

export function link(href: string, props: Props | string | null, ...children: Child[]): HTMLAnchorElement {
  const a = h('a', props, ...children);
  a.href = href;
  if (/^https?:\/\//.test(href) && !href.startsWith(location.origin)) {
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
  }
  return a;
}

export function loadingState(): HTMLElement {
  return h('div', 'state state-loading', h('span', 'state-bar'), h('span', { text: t('common.loading') }));
}

export function emptyState(key: TranslationKey = 'common.empty'): HTMLElement {
  return h('div', 'state state-empty', h('span', 'state-mark', '∅'), h('span', { text: t(key) }));
}

export function errorState(onRetry: () => void, message?: string): HTMLElement {
  return h(
    'div',
    'state state-error',
    h('span', 'state-mark', '!'),
    h('span', { text: message ?? t('common.error') }),
    h('button', { class: 'btn btn-ghost btn-sm', text: t('common.retry'), attrs: { type: 'button' }, on: { click: onRetry } }),
  );
}

/** Splits text into paragraphs; supports **bold** and bare http(s) links. Safe: text nodes only. */
export function richText(text: string | null | undefined, cls = 'rich'): HTMLElement {
  const root = h('div', cls);
  if (!text) return root;
  const paragraphs = text.replace(/\r\n/g, '\n').split(/\n{2,}/);
  for (const para of paragraphs) {
    const p = h('p');
    const lines = para.split('\n');
    lines.forEach((line, i) => {
      appendInline(p, line);
      if (i < lines.length - 1) p.append(h('br'));
    });
    root.append(p);
  }
  return root;
}

function appendInline(parent: HTMLElement, line: string): void {
  const re = /(\*\*[^*]+\*\*|https?:\/\/[^\s<>"]+)/g;
  let last = 0;
  for (const m of line.matchAll(re)) {
    const idx = m.index ?? 0;
    if (idx > last) parent.append(line.slice(last, idx));
    const token = m[0];
    if (token.startsWith('**')) parent.append(h('strong', { text: token.slice(2, -2) }));
    else parent.append(link(token, { class: 'inline-link', text: token.replace(/^https?:\/\//, '') }));
    last = idx + token.length;
  }
  if (last < line.length) parent.append(line.slice(last));
}

/** Plain-text preview without markup. */
export function plain(text: string | null | undefined, max = 200): string {
  if (!text) return '';
  const s = text.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

export function youtubeId(url: string | null | undefined): string | null {
  if (!url) return null;
  const m = /(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/|live\/)|youtu\.be\/)([\w-]{11})/.exec(url);
  return m?.[1] ?? null;
}

export function pad(n: number, size = 2): string {
  return String(n).padStart(size, '0');
}

/** Rebinds a button by cloning it — drops listeners from previous mounts. */
export function rebind<T extends HTMLElement>(id: string, event: string, handler: (e: Event) => void): T | null {
  const el = document.getElementById(id);
  if (!el) return null;
  const fresh = el.cloneNode(true) as T;
  el.replaceWith(fresh);
  fresh.addEventListener(event, handler);
  return fresh;
}
